import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { RadioGroupField } from 'components/form';
import { useForm, useWatch } from 'react-hook-form';
import { ListEmpty } from 'components/shared/List';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { bpfAPI } from '../../api';
import { collectReportFilters } from '../../lib/';
import { ReportFilters } from './ReportsFilters';
import { ReportsSummary } from './ReportsSummary';
import { FullReportsListing } from './FullReportsListing';
import { GROUP_BY_OPTIONS, REPORT_FILTERS } from '../../constants';
import { BPFReportAPINameSpase, FullReportResponse, ReportsListingFilters } from '../../types';

export const FullReports = () => {
  const [filters, setFilters] = useState<ReportsListingFilters>(REPORT_FILTERS);

  const groupByForm = useForm({ defaultValues: { groupBy: 'sales_representative_id' } });
  const groupBy = useWatch({ control: groupByForm.control, name: 'groupBy' });

  const variables = {
    input: { groupBy, filters: collectReportFilters(filters) },
  };

  const { data, loading, error, previousData } = useQuery<BPFReportAPINameSpase<FullReportResponse>>(
    bpfAPI.report.getReport,
    {
      notifyOnNetworkStatusChange: true,
      variables: variables,
    }
  );

  const summary = (data ?? previousData)?.bpf?.report?.result?.entity?.summary;
  const list = (data ?? previousData)?.bpf?.report?.result?.entity?.list ?? [];

  const handleFiltersSubscribe = (filters: ReportsListingFilters) => {
    setFilters(filters);
  };

  return (
    <RenderWithFallback isLoading={loading && !previousData} error={error}>
      <Stack gap="1rem" height={!list.length ? 'calc(100% - 1rem)' : 'auto'}>
        <ReportFilters onSubscribe={handleFiltersSubscribe} />

        <RenderWithCondition condition={!!list.length}>
          <ReportsSummary summary={summary} />
        </RenderWithCondition>

        <RadioGroupField
          row
          name="groupBy"
          label="Group by:"
          options={GROUP_BY_OPTIONS}
          control={groupByForm.control}
        />

        <Box sx={{ boxShadow: ({ shadows }) => shadows[1], height: '100%' }}>
          <RenderWithCondition condition={!!list.length} fallBack={<ListEmpty />}>
            <FullReportsListing list={list} loading={loading} />
          </RenderWithCondition>
        </Box>
      </Stack>
    </RenderWithFallback>
  );
};
